import { ChangeDetectionStrategy, Component, Inject, inject, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { Client, OnePBreakInOutReadDTO, UpdateBreakInOutCommentFieldsCommand } from '../../../api/api-clientInformation';

export interface EditBreakInOutDialogData {
  breakInOut: OnePBreakInOutReadDTO;
}

@Component({
  selector: 'app-edit-break-in-out-dialog',
  standalone: false,
  templateUrl: './edit-break-in-out-dialog.component.html',
  styleUrl: './edit-break-in-out-dialog.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditBreakInOutDialogComponent {
  apiClient = inject(Client);
  private readonly toastrService = inject(ToastrService);
  private readonly translate = inject(TranslateService);

  readonly maxJustificationLength = 4000;
  readonly maxStatusCommentLength = 2000;
  readonly editForm: FormGroup;

  private readonly breakInOut: OnePBreakInOutReadDTO;
  isSaving = false;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<EditBreakInOutDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditBreakInOutDialogData
  ) {
    this.breakInOut = data.breakInOut;
    this.editForm = this.fb.group({
      justification: [this.breakInOut.justification ?? ''],
      statusComment: [this.breakInOut.statusComment ?? ''],
    });
  }

  get hasChanges(): boolean {
    const values = this.editForm.value;
    return (
      (values.justification ?? '') !== (this.breakInOut.justification ?? '') ||
      (values.statusComment ?? '') !== (this.breakInOut.statusComment ?? '')
    );
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    if (!this.hasChanges || this.isSaving) {
      return;
    }

    const values = this.editForm.value;
    // decisionComment must always be sent, otherwise the endpoint clears it.
    const command = UpdateBreakInOutCommentFieldsCommand.fromJS({
      breakInOutId: this.breakInOut.pk,
      justification: values.justification,
      statusComment: values.statusComment,
      decisionComment: this.breakInOut.decisionComment,
    });

    this.isSaving = true;
    this.apiClient.updateBreakInOutCommentFields(command).subscribe({
      next: () => {
        this.toastrService.success(this.translate.instant('MESSAGES.SUCCESS.BREAK_IN_OUT_DETAILS_UPDATED'));
        this.isSaving = false;
        this.dialogRef.close(true);
      },
      error: () => {
        this.toastrService.error(this.translate.instant('MESSAGES.ERROR.FAILED_UPDATE_BREAK_IN_OUT_DETAILS'));
        this.isSaving = false;
      },
    });
  }
}
